/**
 * Phase 22 Plan 22-03 — ReactivateClientDialog
 *
 * Confirms reactivation of a client via POST /api/clients/:id/reactivate.
 */

import { type JSX, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useReactivateClientMutation } from '@/hooks/useClientQueries';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';

interface ReactivateClientDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly clientId: number;
  readonly clientName: string;
}

export function ReactivateClientDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
}: ReactivateClientDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const reactivateMutation = useReactivateClientMutation();

  function handleConfirm(): void {
    setErrorMessage(null);
    reactivateMutation.mutate(clientId, {
      onSuccess: () => {
        toast.success(`Client "${clientName}" reactivated`);
        onOpenChange(false);
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? (err.detail ?? err.message) : 'Unexpected error';
        setErrorMessage(msg);
        toast.error('Could not reactivate client', { description: msg });
      },
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reactivate Client</AlertDialogTitle>
          <AlertDialogDescription>
            Reactivate <strong>{clientName}</strong>? This client will appear in active lists again.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorMessage !== null && (
          <div
            className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
            data-testid="reactivate-client-error"
          >
            {errorMessage}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={reactivateMutation.isPending}
            data-testid="reactivate-client-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={reactivateMutation.isPending}
            data-testid="reactivate-client-confirm"
          >
            {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
